import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AsyncButton from '@wojtekmaj/react-async-button';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import CheckboxField from '@rewardopl/react-ui/checkbox_field';
import InputField from '@rewardopl/react-ui/input_field';

import { post } from '@rewardopl/utils/network';

import { ButtonWrapper, Form } from '../../login/email/form.styles';

import MarketingControls from '../../../common/marketing_controls';
import MarketingDisclaimers from '../../../common/marketing_disclaimers';
import PrivacyPolicyLink from '../../../privacy_policy_link';
import TermsAndConditionsLink from '../../../terms_and_conditions_link';
import EmailTypoDialog from '../../register/email_typo_dialog';

import useLocationParam from '../../../../hooks/useLocationParam';

import { correctEmailTypo } from '../../../../utils/email';
import { isPasswordValid } from '../../../../utils/password';

const pendingConfig = {
  disabled: true,
};

export default function RegisterWithDetailsStepRegisterForm() {
  const location = useLocation();
  const [referralId] = useLocationParam('referral_id');
  const navigate = useNavigate();
  const registerErrorString = useTranslation('Failed to register');
  const userAlreadyExistsString = useTranslation('User already exists');
  const passwordInvalidString = useTranslation(
    'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
  );

  const { email: stateEmail = '', returnPath = '/' } = (location.state || {}) as {
    email?: string;
    returnPath?: string;
  };

  const [email, setEmail] = useState(stateEmail);
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingEmailAccepted, setMarketingEmailAccepted] = useState(false);
  const [marketingSmsAccepted, setMarketingSmsAccepted] = useState(false);
  const marketingAccepted = marketingEmailAccepted || marketingSmsAccepted;

  const [isEmailTypoDialogOpen, setIsEmailTypoDialogOpen] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null);

  const action = '/api/users/check';

  function onBlur() {
    if (!email) {
      return;
    }

    const suggestedEmail = correctEmailTypo(email);

    if (suggestedEmail) {
      setOriginalEmail(email);
      setSuggestedEmail(suggestedEmail);
      setIsEmailTypoDialogOpen(true);
    }
  }

  function onClose() {
    setIsEmailTypoDialogOpen(false);
  }

  async function onClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.preventDefault();

    const submitButton = event.currentTarget;
    const form = submitButton.form;

    if (!form || !form.reportValidity()) {
      return;
    }

    const formData = new FormData();
    formData.append('email', email);

    try {
      const { exists } = (await post(action, {
        body: formData,
      })) as { exists: boolean };

      if (exists) {
        toast.error(userAlreadyExistsString);
        navigate('/login/email', { state: { email, returnPath } });
        return;
      }

      navigate('../details', {
        state: {
          email,
          mobile,
          password,
          termsAccepted,
          marketingAccepted,
          marketingEmailAccepted,
          marketingSmsAccepted,
          referralId,
          returnPath,
        },
      });
    } catch {
      toast.error(registerErrorString);
    }
  }

  return (
    <>
      <Form action={action} method="POST">
        <InputField
          autoComplete="username"
          label="Email"
          maxLength={320}
          name="email"
          onBlur={onBlur}
          onChange={setEmail}
          placeholder="email@example.com"
          type="email"
          value={email}
        />
        <InputField
          autoComplete="new-password"
          hint="Min. 8 characters, uppercase letter, lowercase letter, number, special character"
          label="Password"
          minLength={8}
          name="password"
          onChange={(nextPassword, event) => {
            setPassword(nextPassword);

            event.target.setCustomValidity(
              isPasswordValid(nextPassword, email) ? '' : passwordInvalidString,
            );
          }}
          placeholder="••••••••"
          type="password"
          value={password}
        />
        <CheckboxField
          label={
            <>
              <T>I have read and agree to the</T> <TermsAndConditionsLink /> <T>and</T>{' '}
              <PrivacyPolicyLink />.
            </>
          }
          name="terms_accepted"
          onChange={() => setTermsAccepted(!termsAccepted)}
          required
        />
        <MarketingControls
          marketingAccepted={marketingAccepted}
          marketingEmailAccepted={marketingEmailAccepted}
          marketingSmsAccepted={marketingSmsAccepted}
          setMarketingEmailAccepted={setMarketingEmailAccepted}
          setMarketingSmsAccepted={setMarketingSmsAccepted}
        />
        {marketingSmsAccepted ? (
          <InputField
            autoComplete="tel"
            label="Mobile"
            name="mobile"
            onChange={setMobile}
            type="tel"
            value={mobile}
          />
        ) : null}
        <MarketingDisclaimers />
        <ButtonWrapper>
          <AsyncButton
            as={Button}
            className="primary"
            onClick={onClick}
            pendingConfig={pendingConfig}
            type="submit"
          >
            <T>Continue</T>
          </AsyncButton>
        </ButtonWrapper>
      </Form>
      {suggestedEmail ? (
        <EmailTypoDialog
          isOpen={isEmailTypoDialogOpen}
          onClose={onClose}
          originalEmail={originalEmail}
          setEmail={setEmail}
          suggestedEmail={suggestedEmail}
        />
      ) : null}
    </>
  );
}
